import React from "react";
import { useIntl } from "react-intl";
import PropTypes from "prop-types";

import { PrimaryButton, PrimaryButtonOutlined } from "@components/Button";
import Card from "@components/Card";
import Div from "@components/Div";
import { H4 } from "@components/Heading";
import {
  InputDate,
  InputDropdown,
  InputMultiselect,
  InputRadioGroup,
} from "@components/Input";
import InputText from "@components/InputText";

import { INPUT } from "@utils/constant";
import { APPLICATION_TYPE } from "@utils/enum";

import {
  DISCOUNT_TYPE_RADIO_OPTIONS,
  APPLICATION_TYPE_RADIO_OPTIONS,
  COUPON_STATUSES,
} from "./CouponForm.constants";
import { useCouponForm } from "./CouponForm.hooks";

InputText.defaultProps = {
  curved: true,
  isLowercaseLabel: true,
  labelAlignment: "left",
};

const { TEXT, NUMBER } = INPUT.TYPE;

const Form = ({
  isUpdateForm,
  initialData,
  validationSchema,
  onSubmit,
  onCancel,
}) => {
  const { messages } = useIntl();

  const {
    formik,
    filteredProducts,
    handleOnComplete,
    submitButtonLabel,
    handleRadioButtonChange,
  } = useCouponForm({ isUpdateForm, onSubmit, validationSchema, initialData });

  const { dirty, isValid, values, handleChange } = formik;

  return (
    <Div pb={3}>
      <Card width={1} p={3}>
        <Div display="flex" flexDirection="column">
          <H4>{messages.heading_basic_information}</H4>

          <Div width={1} height="1px" bg="var(--grey-lightest)" mt={3} />

          <Div
            width={1}
            display="flex"
            flexDirection={["column", "column", "row"]}
            alignItems="center"
            gridGap={[0, 0, "24px"]}
          >
            <Div width={1}>
              <InputText
                width={1}
                type={TEXT}
                formikProps={formik}
                name="code"
                label={`${messages.label_coupon_code}*`}
                placeholder={`${messages.label_coupon_code}...`}
                value={values?.code}
              />
            </Div>

            <Div
              display="flex"
              width={1}
              flexDirection={["column", "column", "row"]}
              gridGap={[0, 0, "10px"]}
            >
              <InputDate
                width={1}
                formikProps={formik}
                name="valid_from"
                label={`${messages.label_valid_from}*`}
                placeholder={`${messages.placeholder_choose}...`}
                value={values?.valid_from}
                onChange={handleChange}
              />

              <InputDate
                width={1}
                formikProps={formik}
                name="valid_to"
                label={`${messages.label_expire_date}*`}
                placeholder={`${messages.placeholder_choose}...`}
                value={values?.valid_to}
                onChange={handleChange}
              />
            </Div>
          </Div>

          <Div width={1} height="1px" bg="var(--grey-lightest)" mt={3} />

          <Div
            width={1}
            display="flex"
            flexDirection={["column", "column", "row"]}
            gridGap={[0, 0, "24px"]}
          >
            <InputRadioGroup
              width={1}
              formikProps={formik}
              onChange={handleRadioButtonChange}
              name="discount_type"
              label={`${messages.label_discount_type}*`}
              value={values?.discount_type}
              options={DISCOUNT_TYPE_RADIO_OPTIONS}
            />

            <Div width={1}>
              <InputText
                width={1}
                type={NUMBER}
                formikProps={formik}
                name="discount_value"
                label={`${messages.label_size_on_discount}*`}
                placeholder={`${messages.placeholder_size}...`}
                value={values?.discount_value}
              />
            </Div>
          </Div>

          <Div width={1} height="1px" bg="var(--grey-lightest)" mt={3} />

          <Div
            width={1}
            display="flex"
            flexDirection={["column", "column", "row"]}
            gridGap={[0, 0, "24px"]}
          >
            <InputRadioGroup
              width={1}
              formikProps={formik}
              onChange={handleRadioButtonChange}
              name="application_type"
              label={`${messages.type_label}*`}
              value={values?.application_type}
              options={APPLICATION_TYPE_RADIO_OPTIONS}
            />

            <Div width={1}>
              <InputText
                width={1}
                type={NUMBER}
                formikProps={formik}
                name="max_allowed_usages"
                label={`${messages.label_number_of_uses}*`}
                placeholder={`${messages.placeholder_number}...`}
                value={values?.max_allowed_usages}
              />
            </Div>
          </Div>

          <Div width={1} height="1px" bg="var(--grey-lightest)" mt={3} />

          <InputMultiselect
            formikProps={formik}
            onChange={handleChange}
            name="products"
            label={`${messages.label_products_for_discount}*`}
            placeholder={`${messages.placeholder_choose}...`}
            value={values?.products}
            options={filteredProducts}
            optionLabel="name"
            disabled={
              values?.application_type === APPLICATION_TYPE.ENTIRE_ORDER
            }
            onComplete={handleOnComplete}
          />

          {isUpdateForm && (
            <InputDropdown
              formikProps={formik}
              onChange={handleChange}
              name="active"
              label={messages.label_status}
              placeholder={messages.label_status}
              value={values?.active}
              options={COUPON_STATUSES}
              optionLabel="name"
            />
          )}
        </Div>
      </Card>
      <Div
        mt={4}
        display="flex"
        flexDirection={["column", "column", "row", "row"]}
        gridGap={[3, 3, 4, 4]}
        justifyContent={isUpdateForm ? "center" : "flex-start"}
      >
        <PrimaryButton
          type="submit"
          onClick={formik.handleSubmit}
          label={submitButtonLabel}
          minWidth="150px"
          disabled={!isValid || (isUpdateForm && !dirty)}
        />
        <PrimaryButtonOutlined
          type="button"
          onClick={onCancel}
          label={messages.label_cancel}
          minWidth="150px"
        />
      </Div>
    </Div>
  );
};

Form.propTypes = {
  isUpdateForm: PropTypes.bool,
  initialData: PropTypes.object,
  validationSchema: PropTypes.object,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
};

export default Form;
